<script setup lang="ts">
import { computed, onBeforeUnmount, ref, useTemplateRef } from 'vue'
import { resolvePresetModelUrl, type PresetModel } from '../examples/presetModels'
import {
  registerLocalDirectoryEntry,
  registerLocalModelDirectory,
} from '../utils/localVirtualFiles'

const props = withDefaults(
  defineProps<{
    modelValue: string
    title?: string
    subtitle?: string
    badge?: string
    hint?: string
    loading?: boolean
    canClear?: boolean
    clearLabel?: string
    loadLabel?: string
    allowDirectory?: boolean
    allowFile?: boolean
    directoryEntryNames?: string[]
    directoryPickLabel?: string
    fileAccept?: string
    fileEntryExtensions?: string[]
    filePickLabel?: string
    presetModels?: PresetModel[]
  }>(),
  {
    title: '加载模型',
    subtitle: '通过目录选择加载场景模型',
    badge: '场景模型',
    hint: '',
    loading: false,
    canClear: true,
    clearLabel: '清空场景',
    loadLabel: '加载模型',
    allowDirectory: true,
    allowFile: false,
    directoryEntryNames: () => [],
    directoryPickLabel: '选择模型目录',
    fileAccept: '',
    fileEntryExtensions: () => [],
    filePickLabel: '选择模型文件',
    presetModels: () => [],
  },
)

const emit = defineEmits<{
  (event: 'update:modelValue', value: string): void
  (event: 'submit-load'): void
  (event: 'clear-scene'): void
}>()

const directoryInputRef = useTemplateRef('directoryInputRef')
const fileInputRef = useTemplateRef('fileInputRef')
const pickerNotice = ref('')
let releaseLocalSession: (() => void) | null = null

const selectionSummary = computed(() => {
  if (pickerNotice.value) {
    return pickerNotice.value
  }

  return '尚未重新选择本地模型目录，当前会沿用示例默认模型或上一次成功选择的结果。'
})

function openDirectoryPicker() {
  directoryInputRef.value?.click()
}

function openFilePicker() {
  fileInputRef.value?.click()
}

function submitLoad() {
  emit('submit-load')
}

function replaceLocalSession(release?: () => void): void {
  releaseLocalSession?.()
  releaseLocalSession = release ?? null
}

function clearScene() {
  replaceLocalSession()
  emit('clear-scene')
}

function loadPresetModel(preset: PresetModel) {
  replaceLocalSession()
  const url = resolvePresetModelUrl(preset.path)
  pickerNotice.value = `已选择预设模型：${preset.label}`
  emit('update:modelValue', url)
  emit('submit-load')
}

function handleDirectorySelection(event: Event) {
  const input = event.target as HTMLInputElement | null
  const files = input?.files

  if (!input || !files || files.length === 0) {
    return
  }

  const { entryUrl, entryPath, totalFiles, release } = registerLocalModelDirectory(
    files,
    props.directoryEntryNames,
  )

  if (entryUrl && entryPath) {
    replaceLocalSession(release)
    pickerNotice.value = `已选择本地目录，定位到 ${entryPath}，共 ${totalFiles} 个文件。`
    emit('update:modelValue', entryUrl)
  } else {
    const expected = props.directoryEntryNames.join(' / ') || '入口文件'
    pickerNotice.value = `已选择本地目录，但未找到 ${expected}。`
  }

  input.value = ''
}

function handleFileSelection(event: Event) {
  const input = event.target as HTMLInputElement | null
  const files = input?.files

  if (!input || !files || files.length === 0) {
    return
  }

  const { entryUrl, entryPath, release } = registerLocalDirectoryEntry(files, {
    entryExtensions: props.fileEntryExtensions,
  })

  if (entryUrl && entryPath) {
    replaceLocalSession(release)
    pickerNotice.value = `已选择本地文件：${entryPath}`
    emit('update:modelValue', entryUrl)
  } else {
    const expected = props.fileEntryExtensions.map((item) => `.${item}`).join(' / ')
    pickerNotice.value = `已选择本地文件，但未找到匹配格式。期望文件：${expected || '支持的模型文件'}。`
  }

  input.value = ''
}

onBeforeUnmount(() => replaceLocalSession())
</script>

<template>
  <details class="panel-section" open>
    <summary class="panel-section-summary">
      <span class="summary-main">
        <span class="panel-section-title">{{ props.title }}</span>
        <span class="panel-section-subtitle">{{ props.subtitle }}</span>
      </span>
      <span class="summary-side">
        <span class="summary-count">{{ props.badge }}</span>
        <span class="summary-chevron" aria-hidden="true">v</span>
      </span>
    </summary>

    <div class="panel-section-body stacked-panel-body">
      <p v-if="props.hint" class="panel-note">{{ props.hint }}</p>

      <div class="panel-selection-summary">
        <span class="panel-selection-label">当前选择</span>
        <p class="panel-selection-text">{{ selectionSummary }}</p>
      </div>

      <div v-if="props.presetModels.length > 0" class="preset-models">
        <span class="panel-selection-label">预设模型</span>
        <div class="button-grid">
          <button
            v-for="preset in props.presetModels"
            :key="preset.path"
            class="panel-button panel-button-subtle"
            type="button"
            :disabled="props.loading"
            @click.stop="loadPresetModel(preset)"
          >
            {{ preset.label }}
          </button>
        </div>
      </div>

      <slot name="options" />

      <div class="button-grid">
        <button
          v-if="props.allowDirectory"
          class="panel-button panel-button-subtle"
          type="button"
          @click.stop="openDirectoryPicker"
        >
          {{ props.directoryPickLabel }}
        </button>
        <button
          v-if="props.allowFile"
          class="panel-button panel-button-subtle"
          type="button"
          @click.stop="openFilePicker"
        >
          {{ props.filePickLabel }}
        </button>
        <button
          class="panel-button panel-button-subtle"
          type="button"
          :disabled="!props.canClear"
          @click.stop="clearScene"
        >
          {{ props.clearLabel }}
        </button>
      </div>

      <button
        class="panel-button panel-button-wide"
        type="button"
        :disabled="props.loading"
        @click.stop="submitLoad"
      >
        {{ props.loading ? '加载中...' : props.loadLabel }}
      </button>
    </div>
  </details>

  <input
    v-if="props.allowDirectory"
    ref="directoryInputRef"
    class="loader-file-input"
    type="file"
    webkitdirectory
    directory
    multiple
    @change="handleDirectorySelection"
  />

  <input
    v-if="props.allowFile"
    ref="fileInputRef"
    class="loader-file-input"
    type="file"
    :accept="props.fileAccept"
    @change="handleFileSelection"
  />
</template>

<style scoped>
.loader-file-input {
  display: none;
}

.preset-models {
  display: grid;
  gap: 8px;
}

.panel-selection-summary {
  display: grid;
  gap: 8px;
  padding: 12px 14px;
  border-radius: 16px;
  border: 1px solid rgba(150, 170, 255, 0.16);
  background:
    linear-gradient(180deg, rgba(11, 16, 28, 0.96), rgba(18, 25, 40, 0.88));
}

.panel-selection-label {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(184, 201, 255, 0.74);
}

.panel-selection-text {
  margin: 0;
  font-size: 12px;
  line-height: 1.55;
  color: rgba(238, 244, 255, 0.86);
  word-break: break-word;
}
</style>
